import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import RejectReasonPicker from "./RejectReasonPicker";
import { Check, X, Download, RotateCw, FileText } from "lucide-react";

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;

const DOC_ITEM = {
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  pending: "bg-amber-100 text-amber-800",
};

export function friendlyFilename(applicantName, doc) {
  const ext = (doc.filename || "").split(".").pop()?.toLowerCase() || "pdf";
  const base = `${applicantName || "Applicant"}_${doc.key || doc.title || "document"}`
    .replace(/[^\w\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return `${base}.${ext}`;
}

/**
 * Full-size in-app document preview — image (zoom/rotate) or embedded PDF —
 * with fast-action Approve/Reject in the footer so a reviewer never has to
 * leave the document to act on it. `doc` is one entry from the Document
 * Verification drawer's `GET /applications/:id/documents` response shape
 * ({id, key, title, file_url, file_type, filename, status, metadata, ...}).
 */
export default function DocumentPreviewModal({
  doc, applicantName, filesOrigin, onClose, onApprove, onReject, busy,
}) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  // Reset zoom/rotate/reject-form state whenever a different document opens.
  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setRejecting(false);
    setReason(doc?.rejection_reason || "");
  }, [doc?.id]);

  if (!doc) return null;

  const isImage = (doc.file_type || "").startsWith("image/");
  const isPdf = (doc.file_type || "") === "application/pdf";
  const viewUrl = `${filesOrigin}${doc.file_url}`;
  const downloadUrl = `${filesOrigin}${doc.file_url}?download=true&filename=${encodeURIComponent(friendlyFilename(applicantName, doc))}`;

  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => { setZoom(1); setRotation(0); };
  const rotate = () => setRotation((r) => (r + 90) % 360);

  const confirmReject = () => {
    if (!reason.trim()) return;
    onReject(doc.id, reason.trim());
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col gap-0 overflow-hidden"
        data-testid="document-preview-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-3.5 pr-12">
          <div className="min-w-0">
            <DialogTitle className="text-base font-semibold text-slate-800 truncate">
              {doc.title}{applicantName ? ` — ${applicantName}` : ""}
            </DialogTitle>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-slate-500">
              <Badge className={DOC_ITEM[doc.status] || "bg-slate-100 text-slate-600"}>{doc.status}</Badge>
              {doc.metadata?.board && <span>Board: <b className="text-slate-700">{doc.metadata.board}</b></span>}
              {doc.metadata?.year && <span>Passing year: <b className="text-slate-700">{doc.metadata.year}</b></span>}
              {doc.metadata?.claimed_score && <span>Claimed score: <b className="text-slate-700">{doc.metadata.claimed_score}</b></span>}
            </div>
          </div>
          {isImage && (
            <div className="flex items-center gap-1 shrink-0" data-testid="preview-zoom-controls">
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={zoomOut} disabled={zoom <= ZOOM_MIN} title="Zoom out">−</Button>
              <span className="text-xs text-slate-500 w-11 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={zoomIn} disabled={zoom >= ZOOM_MAX} title="Zoom in">+</Button>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={rotate} title="Rotate 90°"><RotateCw className="w-3.5 h-3.5" /></Button>
              <Button variant="outline" size="sm" className="h-8 px-2 text-xs" onClick={zoomReset}>Reset</Button>
            </div>
          )}
        </div>

        {/* Viewer */}
        <div className="flex-1 min-h-0 overflow-auto bg-slate-100 flex items-center justify-center">
          {isImage ? (
            <img
              src={viewUrl}
              alt={doc.title}
              className="max-w-none transition-transform duration-150"
              style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }}
              data-testid="preview-image"
            />
          ) : isPdf ? (
            <iframe src={viewUrl} title={doc.title} className="w-full h-full border-0" data-testid="preview-pdf" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-slate-400 p-10">
              <FileText className="w-10 h-10" />
              <p className="text-sm">Preview isn't available for this file type.</p>
              <a href={downloadUrl} className="text-sm text-blue-600 underline">Download it instead</a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 py-3.5">
          {rejecting ? (
            <div className="flex items-end gap-2" data-testid="preview-reject-form">
              <div className="flex-1">
                <RejectReasonPicker value={reason} onChange={setReason} testIdPrefix="preview-reject-reason" />
              </div>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 shrink-0" disabled={busy || !reason.trim()}
                data-testid="preview-reject-confirm" onClick={confirmReject}>Confirm rejection</Button>
              <Button size="sm" variant="ghost" className="shrink-0" onClick={() => setRejecting(false)}>Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="text-emerald-700" disabled={busy || doc.status === "approved"}
                data-testid="preview-approve" onClick={() => onApprove(doc.id)}>
                <Check className="w-3.5 h-3.5 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="text-red-600" disabled={busy}
                data-testid="preview-reject" onClick={() => setRejecting(true)}>
                <X className="w-3.5 h-3.5 mr-1" /> {doc.status === "rejected" ? "Edit rejection" : "Reject"}
              </Button>
              <a href={downloadUrl} className="ml-auto" data-testid="preview-download">
                <Button size="sm" variant="outline">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download
                </Button>
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
